/**
 * RFIDistributionSection Component
 * Distribution list management for RFIs
 * BuilderBel Construction Project Management System
 */

import { Mail, Plus, Trash2, Users, X } from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { useToast } from '../../contexts/ToastContext';
import {
  useAddRFIDistributionMutation,
  useRemoveRFIDistributionMutation,
} from '../../services/rfiApi';
import type { RFI, RFIDistribution } from '../../types/rfi';
import type { ProjectMember } from '../../types';
import { SearchableDropdown } from '../forms';

interface RFIDistributionSectionProps {
  projectId: string;
  rfi: RFI;
  teamMembers: ProjectMember[];
  canEdit: boolean;
  onSuccess?: () => void;
}

export const RFIDistributionSection: React.FC<RFIDistributionSectionProps> = ({
  projectId,
  rfi,
  teamMembers,
  canEdit,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [addDistribution, { isLoading: isAdding }] = useAddRFIDistributionMutation();
  const [removeDistribution, { isLoading: isRemoving }] = useRemoveRFIDistributionMutation();

  const isLoading = isAdding || isRemoving;

  // Filter out already added members
  const availableMembers = useMemo(() => {
    const distributionUserIds = (rfi.distribution || []).map(d => d.userId);
    return teamMembers.filter(m => !distributionUserIds.includes(m.userId));
  }, [teamMembers, rfi.distribution]);

  // Team member options for dropdown
  const teamMemberOptions = useMemo(() => {
    return availableMembers.map(member => ({
      value: member.userId,
      label: member.user ? `${member.user.firstName} ${member.user.lastName}` : member.userId,
      description: member.role,
    }));
  }, [availableMembers]);

  // Handle add distribution
  const handleAddDistribution = async () => {
    if (!selectedUserId) return;

    const member = teamMembers.find(m => m.userId === selectedUserId);
    const option = teamMemberOptions.find(o => o.value === selectedUserId);
    if (!member && !option) return;

    try {
      await addDistribution({
        projectId,
        rfiId: rfi._id,
        data: {
          userId: selectedUserId,
          userName: member?.user
            ? `${member.user.firstName} ${member.user.lastName}`
            : option?.label || selectedUserId,
          email: member?.user?.email || '',
          company: member?.user?.title || undefined,
          role: member?.role || option?.description || '',
        },
      }).unwrap();

      success('Added to distribution list');
      setShowAddModal(false);
      setSelectedUserId('');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to add to distribution');
    }
  };

  // Handle remove distribution
  const handleRemoveDistribution = async (distributionId: string) => {
    if (!window.confirm('Remove from distribution list?')) return;

    try {
      await removeDistribution({
        projectId,
        rfiId: rfi._id,
        distributionId,
      }).unwrap();

      success('Removed from distribution list');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to remove from distribution');
    }
  };

  // Get initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const distribution = rfi.distribution || [];

  return (
    <div className="space-y-4">
      {/* Distribution List */}
      {distribution.length > 0 ? (
        <div className="space-y-2">
          {distribution.map((item: RFIDistribution) => (
            <div
              key={item._id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-brand-700">
                    {getInitials(item.userName)}
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.userName}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {item.email}
                    </span>
                    {item.role && (
                      <>
                        <span>•</span>
                        <span>{item.role}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Added {formatDate(item.addedAt)}</span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveDistribution(item._id || '')}
                    disabled={isLoading}
                    className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 text-center">
          <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No distribution list</p>
          <p className="text-xs text-gray-400">
            Add team members to receive notifications about this RFI
          </p>
        </div>
      )}

      {/* Add Button */}
      {canEdit && (
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          disabled={availableMembers.length === 0}
          className="w-full py-2 px-4 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          <span>Add to Distribution</span>
        </button>
      )}

      {/* Add Distribution Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setShowAddModal(false);
              setSelectedUserId('');
            }}
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Add to Distribution</h3>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedUserId('');
                }}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <SearchableDropdown
                label="Select Team Member"
                placeholder="Search team members..."
                options={teamMemberOptions}
                value={selectedUserId}
                onChange={setSelectedUserId}
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedUserId('');
                  }}
                  disabled={isAdding}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddDistribution}
                  disabled={!selectedUserId || isAdding}
                  className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {isAdding ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RFIDistributionSection;
